import React, { useState, useEffect } from 'react';

export interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
    value?: number | null;
    onChange: (value: number) => void;
    /**
     * The value to inject when the input is physically empty.
     * Also the value that gets cleared when focusing the input.
     * @default 0
     */
    fallbackValue?: number;
}

export function NumericInput({
    value,
    onChange,
    fallbackValue = 0,
    onFocus,
    onBlur,
    onWheel,
    ...props
}: NumericInputProps) {
    const [localStr, setLocalStr] = useState(value != null ? String(value) : '');

    // Sync from props, but careful not to wipe out valid "in-progress" typing strings like "1."
    useEffect(() => {
        const parsedLocal = parseFloat(localStr);
        // Only sync from parent if the parent value disagrees with our local parsed value
        // AND localStr isn't currently empty (which occurs when parent falls back to default).
        if (localStr === '' && (value === fallbackValue || value == null)) return;

        // If parent passed a value that matches our numeric representation exactly, ignore it
        // so we don't clobber trailing formatting like "1.0" or "1." being typed.
        if (!isNaN(parsedLocal) && parsedLocal === value && localStr.trim() !== '') return;

        setLocalStr(value != null ? String(value) : '');
    }, [value, fallbackValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setLocalStr(val);

        if (val === '') {
            onChange(fallbackValue);
        } else {
            const parsed = parseFloat(val);
            if (!isNaN(parsed)) {
                onChange(parsed);
            }
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (parseFloat(localStr === '' ? String(fallbackValue) : localStr) === fallbackValue) {
            setLocalStr('');
        }
        onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (localStr === '') {
            setLocalStr(String(fallbackValue));
            onChange(fallbackValue);
        }
        onBlur?.(e);
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
        onWheel?.(e);
    };

    return (
        <input
            {...props}
            type="number"
            value={localStr}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onWheel={handleWheel}
        />
    );
}
